import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BfAuthService } from '@brafton/skynet-angular-security-service';

/**
 * The author object stamped onto feedback items. Matches the `createdBy` shape
 * the rest of the app already reads (a `{ name, email }` record) — see
 * `ProjectDetailPage.toUser` / `exportCreatedBy`.
 */
export interface FeedbackAuthor {
  name?: string;
  email?: string;
}

/**
 * Exposes the signed-in user's identity from the org auth library, centralising
 * the token → app-user mapping so the avatar, account menu, and `createdBy`
 * stamp all read one source.
 *
 * `BfAuthService` decodes the profile straight out of the Auth0 ID token (no
 * `/userinfo` call) and publishes it on a `BehaviorSubject`. `toSignal` is what
 * makes that usable here: the app is zoneless, so a bare subscription would
 * update state without ever triggering change detection.
 */
@Injectable({ providedIn: 'root' })
export class CurrentUserService {
  private readonly auth = inject(BfAuthService);

  /** Decoded Auth0 ID-token profile. */
  private readonly profile = toSignal(this.auth.profileAsync, { initialValue: null });

  /**
   * Skynet seeds its profile subject with a blank template rather than null, so
   * empty strings are treated as absent.
   */
  readonly name = computed(() => this.profile()?.name || null);
  readonly email = computed(() => this.profile()?.email || null);
  readonly pictureUrl = computed(() => this.profile()?.picture || null);

  /** Display label for the account menu: name, then email, then a placeholder. */
  readonly displayName = computed(() => this.name() ?? this.email() ?? 'My Account');

  /**
   * The `createdBy` author to record on new feedback — the signed-in user's
   * name and email from the token. Fields absent from the token are omitted.
   */
  author(): FeedbackAuthor {
    const author: FeedbackAuthor = {};
    const name = this.name();
    const email = this.email();
    if (name) {
      author.name = name;
    }
    if (email) {
      author.email = email;
    }
    return author;
  }
}
